import i18n from 'i18n';
import { isListEnabled } from 'utils/queryHelpers';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { workersService } from 'services';
import { showNotification } from 'components';
import { NotificationType, QueryContextFromKeys, QueryParamModel, WorkerViewModel } from 'models';
import { SendMessageFormProps } from 'pages/Kozos/Workers/SendMessageDialog';
import { REACT_QUERY_OPTIONS } from 'config/reactQueryConfig';
import { handleDeleteWorkerUserError } from 'utils/errors';

type WorkersQueryContexts = QueryContextFromKeys<typeof workersKeys>;

export const workersKeys = {
  all: [{ scope: 'workers' }] as const,
  lists: () => [{ ...workersKeys.all[0], entity: 'list' }] as const,
  workersList: (params?: QueryParamModel | null) =>
    [{ ...workersKeys.lists()[0], params }] as const,
  workerSearch: (params?: QueryParamModel) => [{ ...workersKeys.lists()[0], params }] as const,
  exportList: (params?: QueryParamModel | null) =>
    [{ ...workersKeys.all[0], entity: 'export', params }] as const,
  latestSyncJob: () => [{ ...workersKeys.all[0], entity: 'latestSyncJob' }] as const,
};

const exportWorkers = async ({ queryKey: [{ params }] }: WorkersQueryContexts['exportList']) =>
  workersService.exportWorkers(params);

const fetchWorkersList = async ({ queryKey: [{ params }] }: WorkersQueryContexts['workersList']) =>
  workersService.getWorkersList(params);

const fetchWorkerSearch = async ({
  queryKey: [{ params }],
}: WorkersQueryContexts['workerSearch']) => workersService.searchWorkers(params);

export const useWorkerPageList = (params?: QueryParamModel | null) => {
  return useQuery({
    queryKey: workersKeys.workersList(params),
    queryFn: fetchWorkersList,
    enabled: isListEnabled(params),
  });
};

export const useWorkerList = (params: QueryParamModel | null = null) => {
  return useQuery({ queryKey: workersKeys.workersList(params), queryFn: fetchWorkersList });
};

export const useWorkerListSearch = (params?: QueryParamModel, enabled = true) => {
  return useQuery({
    queryKey: workersKeys.workerSearch(params),
    queryFn: fetchWorkerSearch,
    enabled,
  });
};

export const useSendMessage = () => {
  return useMutation({
    mutationFn: ({ data }: { data: SendMessageFormProps }) => workersService.sendMessage(data),
  });
};

export const useSyncTask = () => {
  return useMutation({
    mutationFn: (jobId: string) => workersService.getSyncTaskById(jobId),
    meta: {
      [REACT_QUERY_OPTIONS.SKIP_DEFAULT_SUCCESS_CALLBACK]: true,
    },
  });
};

export const useLatestSyncJob = () => {
  return useQuery({
    queryKey: workersKeys.latestSyncJob(),
    queryFn: () => workersService.getLatestSyncJob(),
  });
};

export const useSyncWorkers = () => {
  return useMutation({
    mutationFn: () => {
      return workersService.syncWorkers();
    },
    onSuccess: async (_response: { jobId: string }) => {
      showNotification({
        content: i18n.t('MUNKAVEGZOK.SYNC_WAIT'),
        type: NotificationType.WARNING,
      });
    },
  });
};

export const useExportWorkers = (params?: QueryParamModel | null) => {
  return useQuery({
    queryKey: workersKeys.exportList(params),
    queryFn: exportWorkers,
    enabled: false,
    // The download is a side effect, never rendered - keeping the entry cached only pins memory.
    gcTime: 0,
  });
};

export const useEditWorker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: ID; data: Partial<WorkerViewModel> }) =>
      workersService.editWorker(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workersKeys.lists() });
    },
  });
};

export const useDeleteWorker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: ID) => workersService.deleteWorker(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workersKeys.lists() });
    },
    onError: handleDeleteWorkerUserError,
  });
};
